import React, { useContext, useEffect, useState } from "react";
import {
  Button, Typography, CircularProgress, Card, CardContent, Grid, Box, Paper, TextField, MenuItem, Tooltip, InputAdornment, IconButton, Chip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  deleteRequest, getRequestWithToken, patchRequestWithToken, postRequest, putRequestWithToken
} from "../../utils/ApiRequests";
import "./candidateRoleMappings.scss";
import { blueGrey } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import ModalComponent from "../../components/ModalComponent/ModalComponent";
import { Formik } from "formik";
import * as yup from "yup";
import SureYouWantToDeletModal from "../../components/SureYouWantToDeleteModal/SureYouWantToDeletModal";
import { checkPermission } from "../../utils/userPermissions";

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(blueGrey[50]),
  backgroundColor: "#FFFFFF",
  "&:hover": { backgroundColor: blueGrey[50] },
}));

const nameValidation = yup.object().shape({
  nameEn: yup.string().trim().required("English name is required"),
  nameHi: yup.string().trim(),
  nameHn: yup.string().trim(),
});

const familyValidation = nameValidation.shape({
  familyId: yup.string().trim().required("Family ID is required"),
  uiLabel: yup.string().trim(),
  icon: yup.string().trim(),
  sortOrder: yup.number(),
});

const groupValidation = nameValidation.shape({
  roleGroupId: yup.string().trim().required("Group ID is required"),
  uiLabel: yup.string().trim(),
  sortOrder: yup.number(),
});

const roleValidation = nameValidation.shape({
  roleId: yup.string().trim().required("Role ID is required"),
  uiLabel: yup.string().trim(),
  description: yup.string().trim(),
  aliases: yup.string().trim(),
  sourceSectorId: yup.string().trim(),
  sourceOccupationId: yup.string().trim(),
  sourceJobRoleId: yup.string().trim(),
});

const initialFamily = {
  familyId: "", nameEn: "", nameHi: "", nameHn: "", uiLabel: "", icon: "", sortOrder: 0
};

const initialGroup = {
  roleGroupId: "", nameEn: "", nameHi: "", nameHn: "", uiLabel: "", sortOrder: 0
};

const initialRole = {
  roleId: "", nameEn: "", nameHi: "", nameHn: "", uiLabel: "", description: "", aliases: "",
  sourceSectorId: "", sourceSectorName: "", sourceOccupationId: "", sourceOccupationName: "", sourceJobRoleId: "", sourceJobRoleName: ""
};

export const candidateRoleMappingApiBase = "../v3/admin/candidate-role-mappings";

const includesSearch = (value, search) => String(value || "").toLowerCase().includes(search.toLowerCase());

const aliasesForSearch = (aliases) => {
  if (Array.isArray(aliases)) return aliases;
  if (!aliases || typeof aliases !== "object") return [];
  return Object.values(aliases).flat().filter(Boolean);
};

const sourceLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.sectorNameEn || value.occupationNameEn || value.jobRoleNameEn || value.name || value._id || "";
};

const searchFieldProps = (value, onClear) => ({
  InputProps: {
    endAdornment: value ? (
      <InputAdornment position="end">
        <IconButton aria-label="Clear search" size="small" onClick={onClear}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : null
  }
});

const generateEmojiRange = (start, end) => (
  Array.from({ length: end - start + 1 }, (_, index) => String.fromCodePoint(start + index))
);

const FAMILY_ICON_EMOJIS = [
  ...generateEmojiRange(0x1F300, 0x1F5FF),
  ...generateEmojiRange(0x1F680, 0x1F6FF),
  ...generateEmojiRange(0x1F900, 0x1F9FF),
  ...generateEmojiRange(0x2600, 0x26FF),
  ...generateEmojiRange(0x2700, 0x27BF),
];

const sortByText = (getValue) => (a, b) => String(getValue(a) || "").localeCompare(String(getValue(b) || ""), "en", { sensitivity: "base" });

const getRoleDisplayPayload = (values) => {
  const title = values.uiLabel || values.nameEn || values.roleId || "";
  return {
    display_title: title,
    candidate_display_title: title,
    employer_display_title: title,
    slug: title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  };
};

export const buildCandidateRoleMappingPayload = (type, values, selectedFamily = null, selectedGroup = null) => {
  const base = {
    status: "active",
    family: selectedFamily ? {
      family_id: selectedFamily.family_id,
      family_name: selectedFamily.family_name || selectedFamily.name?.en,
      family_label_hi: selectedFamily.family_label_hi || selectedFamily.name?.hi || null,
      family_label_hn: selectedFamily.family_label_hn || selectedFamily.name?.hn || null,
      family_ui_label: selectedFamily.family_ui_label || selectedFamily.ui_label,
      family_icon: selectedFamily.family_icon || selectedFamily.icon,
      family_sort_order: selectedFamily.family_sort_order || selectedFamily.sort_order || 0
    } : {
      family_id: values.familyId,
      family_name: values.nameEn,
      family_label_hi: values.nameHi || null,
      family_label_hn: values.nameHn || null,
      family_ui_label: values.uiLabel || null,
      family_icon: values.icon || null,
      family_sort_order: values.sortOrder || 0
    },
    role_group: { role_group_id: "", role_group_name: "", role_group_ui_label: null, role_group_sort_order: 0 },
    role: { role_id: "", role_name: "", role_ui_label: null, role_description: null, aliases: { en: [] }, search_keywords: [] }
  };

  if (type === "family") {
    base.family = {
      family_id: values.familyId,
      family_name: values.nameEn,
      family_label_hi: values.nameHi || null,
      family_label_hn: values.nameHn || null,
      family_ui_label: values.uiLabel || null,
      family_icon: values.icon || null,
      family_sort_order: values.sortOrder || 0
    };
  } else if (type === "group") {
    base.role_group = {
      role_group_id: values.roleGroupId,
      role_group_name: values.nameEn,
      role_group_label_hi: values.nameHi || null,
      role_group_label_hn: values.nameHn || null,
      role_group_ui_label: values.uiLabel || null,
      role_group_sort_order: values.sortOrder || 0
    };
  } else if (type === "role") {
    const aliases = values.aliases ? values.aliases.split(",").map((s) => s.trim()).filter(Boolean) : [];
    base.role_group = selectedGroup ? {
      role_group_id: selectedGroup.role_group_id,
      role_group_name: selectedGroup.role_group_name || selectedGroup.name?.en,
      role_group_label_hi: selectedGroup.role_group_label_hi || selectedGroup.name?.hi || null,
      role_group_label_hn: selectedGroup.role_group_label_hn || selectedGroup.name?.hn || null,
      role_group_ui_label: selectedGroup.role_group_ui_label || selectedGroup.ui_label,
      role_group_sort_order: selectedGroup.role_group_sort_order || selectedGroup.sort_order || 0
    } : base.role_group;
    base.role = {
      role_id: values.roleId,
      role_name: values.nameEn,
      role_label_hi: values.nameHi || null,
      role_label_hn: values.nameHn || null,
      role_ui_label: values.uiLabel || null,
      role_description: values.description || null,
      aliases: { en: aliases },
      search_keywords: aliases
    };
    if (values.sourceJobRoleId) {
      base.source_mapping = {
        sector_ref: values.sourceSectorId || null,
        occupation_ref: values.sourceOccupationId || null,
        job_role_ref: values.sourceJobRoleId,
        current_sector: values.sourceSectorName,
        current_occupation: values.sourceOccupationName,
        current_job_role: values.sourceJobRoleName,
        mapped_status: "Mapped",
        mapping_confidence: "High"
      };
    }
    base.display = getRoleDisplayPayload(values);
  }

  return base;
};

const CandidateRoleMappings = () => {
  const { handleSnackBar } = useContext(GlobalContext);

  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [roles, setRoles] = useState([]);

  const [showLoaderFamilies, setShowLoaderFamilies] = useState(false);
  const [showLoaderGroups, setShowLoaderGroups] = useState(false);
  const [showLoaderRoles, setShowLoaderRoles] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [showLoaderSectors, setShowLoaderSectors] = useState(false);
  const [showLoaderOccupations, setShowLoaderOccupations] = useState(false);
  const [showLoaderJobRoles, setShowLoaderJobRoles] = useState(false);

  const [familyModal, setFamilyModal] = useState({ open: false, editId: null, initial: initialFamily });
  const [groupModal, setGroupModal] = useState({ open: false, editId: null, initial: initialGroup });
  const [roleModal, setRoleModal] = useState({ open: false, editId: null, initial: initialRole });
  const [viewModal, setViewModal] = useState({ open: false, type: "", data: null });

  const [deleteModal, setDeleteModal] = useState({ open: false, type: "", id: null, name: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [perms, setPerms] = useState({ canCreate: false, canDelete: false, canUpdate: false });
  const [familySearch, setFamilySearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [roleLinks, setRoleLinks] = useState([]);
  const [unmappedNcoRoles, setUnmappedNcoRoles] = useState([]);
  const [ncoSearch, setNcoSearch] = useState("");
  const [ncoSectorId, setNcoSectorId] = useState("");
  const [ncoOccupationId, setNcoOccupationId] = useState("");
  const [showMappedNcoRoles, setShowMappedNcoRoles] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);

  const apiBase = candidateRoleMappingApiBase;

  useEffect(() => {
    const p = checkPermission("Job Category");
    setPerms(p);
    loadFamilies();
    loadCoverage();
    loadUnmappedNcoRoles();
    loadSourceSectors();
  }, []);

  const handleApiError = (err, msg) => {
    handleSnackBar(true, err?.response?.data?.msg || err?.msg || msg, "error");
  };

  const loadCoverage = async () => {
    try {
      const res = await getRequestWithToken(`${apiBase}/coverage`);
      if (res.data?.status === "Success") setCoverage(res.data.data || null);
    } catch (err) {
      handleApiError(err, "Failed to load NCO mapping coverage");
    }
  };

  const loadUnmappedNcoRoles = async (
    search = "",
    sectorId = ncoSectorId,
    occupationId = ncoOccupationId,
    includeMapped = showMappedNcoRoles
  ) => {
    try {
      const params = { mapped: includeMapped ? "true" : "false", limit: 50, search };
      if (sectorId) params.sector_id = sectorId;
      if (occupationId) params.occupation_id = occupationId;
      const query = new URLSearchParams(params).toString();
      const res = await getRequestWithToken(`${apiBase}/nco-links?${query}`);
      if (res.data?.status === "Success") setUnmappedNcoRoles(res.data?.data?.links || []);
    } catch (err) {
      handleApiError(err, "Failed to load unmapped NCO roles");
    }
  };

  const loadRoleLinks = async (roleItemId) => {
    if (!roleItemId) {
      setRoleLinks([]);
      return;
    }
    try {
      const links = [];
      let page = 1;
      let totalPages = 1;
      do {
        const query = new URLSearchParams({
          mapped: "true", candidate_role_id: roleItemId, limit: 100, page,
        }).toString();
        const res = await getRequestWithToken(`${apiBase}/nco-links?${query}`);
        if (res.data?.status !== "Success") break;
        links.push(...(res.data?.data?.links || []));
        totalPages = res.data?.data?.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);
      setRoleLinks(links);
    } catch (err) {
      handleApiError(err, "Failed to load linked NCO roles");
    }
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    loadRoleLinks(role.roleItemId);
  };

  const refreshMappingWorkspace = async () => {
    await Promise.all([
      loadCoverage(),
      loadUnmappedNcoRoles(ncoSearch, ncoSectorId, ncoOccupationId, showMappedNcoRoles),
      loadRoleLinks(selectedRole?.roleItemId),
    ]);
  };

  const handleMapNcoRole = async (ncoJobRoleId) => {
    if (!selectedRole?.roleItemId) return;
    setMappingLoading(true);
    try {
      const res = await putRequestWithToken(`${apiBase}/nco-links/${ncoJobRoleId}`, {
        role_item_id: selectedRole.roleItemId,
      });
      if (res.data?.status !== "Success") throw new Error(res.data?.msg || "Mapping failed");
      handleSnackBar(true, res.data?.msg || "NCO role mapped successfully", "success");
      await refreshMappingWorkspace();
    } catch (err) {
      handleApiError(err, "Failed to map NCO role");
    } finally {
      setMappingLoading(false);
    }
  };

  const handleUnmapNcoRole = async (ncoJobRoleId) => {
    setMappingLoading(true);
    try {
      const res = await deleteRequest(`${apiBase}/nco-links/${ncoJobRoleId}`);
      if (res.data?.status !== "Success") throw new Error(res.data?.msg || "Unmapping failed");
      handleSnackBar(true, res.data?.msg || "NCO role unmapped successfully", "success");
      await refreshMappingWorkspace();
    } catch (err) {
      handleApiError(err, "Failed to unmap NCO role");
    } finally {
      setMappingLoading(false);
    }
  };

  const getMappingPayloadFromExisting = (mapping) => ({
    status: mapping.status || "active",
    family: mapping.family,
    role_group: mapping.role_group,
    role: mapping.role,
    level: mapping.level || null,
    display: mapping.display || null,
    matching: mapping.matching || null,
    source_mapping: mapping.source_mapping || null,
    jobsyahan_rules: mapping.jobsyahan_rules || null,
  });

  const getFamilyFromValues = (values) => ({
    family_id: values.familyId,
    family_name: values.nameEn,
    family_label_hi: values.nameHi || null,
    family_label_hn: values.nameHn || null,
    family_ui_label: values.uiLabel || null,
    family_icon: values.icon || null,
    family_sort_order: values.sortOrder || 0
  });

  const getGroupFromValues = (values) => ({
    role_group_id: values.roleGroupId,
    role_group_name: values.nameEn,
    role_group_label_hi: values.nameHi || null,
    role_group_label_hn: values.nameHn || null,
    role_group_ui_label: values.uiLabel || null,
    role_group_sort_order: values.sortOrder || 0
  });

  const loadAllMappings = async (params = {}) => {
    const limit = 1000;
    let page = 1;
    let totalPages = 1;
    const mappings = [];

    do {
      const query = new URLSearchParams({ ...params, page, limit }).toString();
      const res = await getRequestWithToken(`${apiBase}?${query}`);
      if (res.data?.status !== "Success") break;
      mappings.push(...(res.data?.data?.mappings || []));
      totalPages = res.data?.data?.pagination?.totalPages || 1;
      page += 1;
    } while (page <= totalPages);

    return mappings;
  };

  const loadFamilies = async () => {
    setShowLoaderFamilies(true);
    try {
      const [raw, familyMasterRes] = await Promise.all([
        loadAllMappings(),
        getRequestWithToken("../v3/admin/families").catch(() => null)
      ]);
      const map = {};
      (familyMasterRes?.data?.data || []).forEach((f) => {
        if (f?._id || f?.family_id) {
          const key = f.family_id || f._id;
          map[key] = {
            family_id: f.family_id,
            family_name: f.family_name,
            family_ui_label: f.family_ui_label,
            family_icon: f.family_icon,
            family_sort_order: f.family_sort_order || 0,
            familyMasterId: f._id
          };
        }
      });
      raw.forEach((m) => {
        const f = m.family;
        if (f?.family_id) {
          map[f.family_id] = { ...map[f.family_id], ...f, mappingId: m._id };
        }
      });
      const nextFamilies = Object.values(map).sort(sortByText((item) => item.family_name || item.name?.en || item.family_id));
      setFamilies(nextFamilies);
      return nextFamilies;
    } catch (err) {
      handleApiError(err, "Failed to load families");
      return [];
    } finally {
      setShowLoaderFamilies(false);
    }
  };

  const loadGroups = async (familyId, familyMasterId = families.find(item => item.family_id === familyId)?.familyMasterId) => {
    if (!familyId) return [];
    setShowLoaderGroups(true);
    try {
      const [raw, groupMasterRes] = await Promise.all([
        loadAllMappings({ family_id: familyId }),
        familyMasterId
          ? getRequestWithToken(`../v3/admin/families/${familyMasterId}/groups`).catch(() => null)
          : Promise.resolve(null),
      ]);
      const map = {};
      (groupMasterRes?.data?.data || []).forEach((g) => {
        map[g.group_id] = {
          role_group_id: g.group_id,
          role_group_name: g.group_name,
          role_group_ui_label: g.group_ui_label,
          role_group_sort_order: g.group_sort_order || 0,
          groupMasterId: g._id,
          familyId,
        };
      });
      raw.forEach((m) => {
        const g = m.role_group;
        if (g?.role_group_id) {
          map[g.role_group_id] = { ...map[g.role_group_id], ...g, mappingId: m._id, familyId };
        }
      });
      const nextGroups = Object.values(map).sort(sortByText((item) => item.role_group_name || item.name?.en || item.role_group_id));
      setGroups(nextGroups);
      return nextGroups;
    } catch (err) {
      handleApiError(err, "Failed to load groups");
      return [];
    } finally {
      setShowLoaderGroups(false);
    }
  };

  const loadRoles = async (groupId, groupMasterId = groups.find(item => item.role_group_id === groupId)?.groupMasterId) => {
    if (!groupId) return [];
    setShowLoaderRoles(true);
    try {
      const [raw, roleMasterRes] = await Promise.all([
        loadAllMappings({ role_group_id: groupId }),
        groupMasterId
          ? getRequestWithToken(`../v3/admin/groups/${groupMasterId}/roles`).catch(() => null)
          : Promise.resolve(null),
      ]);
      const roleMap = {};
      (roleMasterRes?.data?.data || []).forEach((role) => {
        roleMap[role.role_id] = {
          roleItemId: role._id,
          canonicalOnly: true,
          family: selectedFamily,
          role_group: selectedGroup,
          role: {
            role_id: role.role_id,
            role_name: role.role_name,
            role_ui_label: role.role_ui_label,
            aliases: role.aliases || { en: [] },
          },
          status: "active",
        };
      });
      raw.forEach((mapping) => {
        const roleId = mapping.role?.role_id;
        if (!roleId) return;
        roleMap[roleId] = {
          ...roleMap[roleId],
          ...mapping,
          roleItemId: roleMap[roleId]?.roleItemId,
          canonicalOnly: false,
        };
      });
      const nextRoles = Object.values(roleMap)
        .sort(sortByText((item) => item.role?.role_name || item.role?.name?.en || item.role?.role_id));
      setRoles(nextRoles);
      return nextRoles;
    } catch (err) {
      handleApiError(err, "Failed to load roles");
      return [];
    } finally {
      setShowLoaderRoles(false);
    }
  };

  const loadSourceSectors = async () => {
    setShowLoaderSectors(true);
    try {
      const res = await getRequestWithToken("admin/sectors?search=");
      if (res.data?.status === "Success") {
        setSectors(res.data?.data || []);
      }
    } catch (err) {
      handleApiError(err, "Failed to load sectors");
    }
    setShowLoaderSectors(false);
  };

  const loadSourceOccupations = async (sectorId) => {
    if (!sectorId) {
      setOccupations([]);
      return;
    }
    setShowLoaderOccupations(true);
    try {
      const res = await getRequestWithToken(`admin/occupations?sectorId=${sectorId}`);
      if (res.data?.status === "Success") {
        setOccupations(res.data?.data || []);
      }
    } catch (err) {
      handleApiError(err, "Failed to load occupations");
      setOccupations([]);
    }
    setShowLoaderOccupations(false);
  };

  const loadSourceJobRoles = async (occupationId) => {
    if (!occupationId) {
      setJobRoles([]);
      return;
    }
    setShowLoaderJobRoles(true);
    try {
      const res = await getRequestWithToken(`admin/job-roles?occupation=${occupationId}`);
      if (res.data?.status === "Success") {
        setJobRoles(res.data?.data || []);
      }
    } catch (err) {
      handleApiError(err, "Failed to load job roles");
      setJobRoles([]);
    }
    setShowLoaderJobRoles(false);
  };

  const handleSelectFamily = (f) => {
    setSelectedFamily(f);
    setSelectedGroup(null);
    setSelectedRole(null);
    setRoleLinks([]);
    setRoles([]);
    setGroupSearch("");
    setRoleSearch("");
    if (f.isLocalDraft) {
      setGroups([]);
      return;
    }
    loadGroups(f.family_id, f.familyMasterId);
  };

  const handleSelectGroup = (g) => {
    setSelectedGroup(g);
    setSelectedRole(null);
    setRoleLinks([]);
    setRoles([]);
    setRoleSearch("");
    if (g.isLocalDraft) return;
    loadRoles(g.role_group_id, g.groupMasterId);
  };

  const handleSubmitMapping = async (type, values, editId) => {
    try {
      if (!editId && type === "family") {
        const tempFamily = {
          family_id: values.familyId,
          family_name: values.nameEn,
          family_label_hi: values.nameHi || null,
          family_label_hn: values.nameHn || null,
          family_ui_label: values.uiLabel || null,
          family_icon: values.icon || null,
          family_sort_order: values.sortOrder || 0,
          isLocalDraft: true
        };
        setFamilies((prev) => [...prev.filter(item => item.family_id !== tempFamily.family_id), tempFamily]);
        setSelectedFamily(tempFamily);
        setSelectedGroup(null);
        setGroups([]);
        setRoles([]);
        handleSnackBar(true, "Family staged. Add a group and role to save mapping.", "info");
        return;
      }

      if (!editId && type === "group") {
        const tempGroup = {
          role_group_id: values.roleGroupId,
          role_group_name: values.nameEn,
          role_group_label_hi: values.nameHi || null,
          role_group_label_hn: values.nameHn || null,
          role_group_ui_label: values.uiLabel || null,
          role_group_sort_order: values.sortOrder || 0,
          familyId: selectedFamily?.family_id,
          isLocalDraft: true
        };
        setGroups((prev) => [...prev.filter(item => item.role_group_id !== tempGroup.role_group_id), tempGroup]);
        setSelectedGroup(tempGroup);
        setRoles([]);
        handleSnackBar(true, "Group staged. Add a role to save mapping.", "info");
        return;
      }

      if (editId && type === "family") {
        const oldFamilyId = values.originalFamilyId || values.familyId;
        const family = getFamilyFromValues(values);
        const mappings = await loadAllMappings({ family_id: oldFamilyId });
        await Promise.all(mappings.map((mapping) => patchRequestWithToken(`${apiBase}/${mapping._id}`, {
          ...getMappingPayloadFromExisting(mapping),
          family
        })));
        handleSnackBar(true, "Family updated successfully", "success");
        const nextFamilies = await loadFamilies();
        const nextFamily = nextFamilies.find(item => item.family_id === family.family_id) || null;
        setSelectedFamily(nextFamily);
        setSelectedGroup(null);
        setGroups([]);
        setRoles([]);
        return;
      }

      if (editId && type === "group") {
        const oldGroupId = values.originalRoleGroupId || values.roleGroupId;
        const role_group = getGroupFromValues(values);
        const mappings = await loadAllMappings({ role_group_id: oldGroupId });
        await Promise.all(mappings.map((mapping) => patchRequestWithToken(`${apiBase}/${mapping._id}`, {
          ...getMappingPayloadFromExisting(mapping),
          role_group
        })));
        handleSnackBar(true, "Group updated successfully", "success");
        const nextGroups = await loadGroups(selectedFamily?.family_id, selectedFamily?.familyMasterId);
        const nextGroup = nextGroups.find(item => item.role_group_id === role_group.role_group_id) || null;
        setSelectedGroup(nextGroup);
        setSelectedRole(null);
        setRoleLinks([]);
        setRoles([]);
        if (nextGroup) await loadRoles(nextGroup.role_group_id, nextGroup.groupMasterId);
        return;
      }

      const payload = buildCandidateRoleMappingPayload(type, values, selectedFamily, selectedGroup);
      let res;
      if (editId) {
        res = await patchRequestWithToken(`${apiBase}/${editId}`, payload);
      } else {
        res = await postRequest(apiBase, payload);
      }
      if (res.data?.status === "Success") {
        handleSnackBar(true, res.data?.msg || "Saved successfully", "success");
        const savedFamilyId = payload.family?.family_id || selectedFamily?.family_id;
        const savedGroupId = payload.role_group?.role_group_id || selectedGroup?.role_group_id;
        const nextFamilies = await loadFamilies();
        const nextFamily = nextFamilies.find(item => item.family_id === savedFamilyId) || selectedFamily;
        setSelectedFamily(nextFamily);
        const nextGroups = savedFamilyId ? await loadGroups(savedFamilyId, nextFamily?.familyMasterId) : [];
        const nextGroup = nextGroups.find(item => item.role_group_id === savedGroupId) || selectedGroup;
        setSelectedGroup(nextGroup);
        setSelectedRole(null);
        setRoleLinks([]);
        if (savedGroupId) await loadRoles(savedGroupId, nextGroup?.groupMasterId);
        await loadCoverage();
      } else {
        handleSnackBar(true, res.data?.msg || "Failed to save", "error");
      }
    } catch (err) {
      handleApiError(err, "Failed to save");
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteRequest(`${apiBase}/${deleteModal.id}`);
      if (res.data?.status === "Success") {
        handleSnackBar(true, res.data?.msg || "Deleted successfully", "success");
        setDeleteModal({ open: false, type: "", id: null, name: "" });
        loadFamilies();
        setSelectedFamily(null);
        setSelectedGroup(null);
        setSelectedRole(null);
        setRoleLinks([]);
        setGroups([]);
        setRoles([]);
      } else {
        handleSnackBar(true, res.data?.msg || "Delete failed", "error");
      }
    } catch (err) {
      handleApiError(err, "Delete failed");
    }
    setDeleteLoading(false);
  };

  const openCreateFamily = () => setFamilyModal({ open: true, editId: null, initial: initialFamily });
  const openEditFamily = (f) => setFamilyModal({
    open: true, editId: f.mappingId,
    initial: {
      familyId: f.family_id || "",
      originalFamilyId: f.family_id || "",
      nameEn: f.family_name || f.name?.en || "",
      nameHi: f.family_label_hi || f.name?.hi || "",
      nameHn: f.family_label_hn || f.name?.hn || "",
      uiLabel: f.family_ui_label || f.ui_label || "",
      icon: f.family_icon || f.icon || "",
      sortOrder: f.family_sort_order || f.sort_order || 0
    }
  });
  const openDeleteFamily = (f) => setDeleteModal({ open: true, type: "family", id: f.mappingId, name: f.family_name || f.name?.en || f.family_id });

  const openCreateGroup = () => setGroupModal({ open: true, editId: null, initial: initialGroup });
  const openEditGroup = (g) => setGroupModal({
    open: true, editId: g.mappingId,
    initial: {
      roleGroupId: g.role_group_id || "",
      originalRoleGroupId: g.role_group_id || "",
      nameEn: g.role_group_name || g.name?.en || "",
      nameHi: g.role_group_label_hi || g.name?.hi || "",
      nameHn: g.role_group_label_hn || g.name?.hn || "",
      uiLabel: g.role_group_ui_label || g.ui_label || "",
      sortOrder: g.role_group_sort_order || g.sort_order || 0
    }
  });
  const openDeleteGroup = (g) => setDeleteModal({ open: true, type: "group", id: g.mappingId, name: g.role_group_name || g.name?.en || g.role_group_id });

  const openCreateRole = () => {
    setOccupations([]);
    setJobRoles([]);
    loadSourceSectors();
    setRoleModal({ open: true, editId: null, initial: initialRole });
  };
  const openEditRole = (r) => {
    const sourceMapping = r.source_mapping || {};
    const sourceSectorId = sourceMapping.sector_ref?._id || sourceMapping.sector_ref || "";
    const sourceOccupationId = sourceMapping.occupation_ref?._id || sourceMapping.occupation_ref || "";
    const sourceJobRoleId = sourceMapping.job_role_ref?._id || sourceMapping.job_role_ref || "";
    loadSourceSectors();
    if (sourceSectorId) loadSourceOccupations(sourceSectorId);
    if (sourceOccupationId) loadSourceJobRoles(sourceOccupationId);
    setRoleModal({
      open: true, editId: r._id,
      initial: {
        roleId: r.role?.role_id || "",
        nameEn: r.role?.name?.en || r.role?.role_name || "",
        nameHi: r.role?.name?.hi || r.role?.role_label_hi || "",
        nameHn: r.role?.name?.hn || r.role?.role_label_hn || "",
        uiLabel: r.role?.ui_label || r.role?.role_ui_label || "",
        description: r.role?.description || r.role?.role_description || "",
        aliases: (Array.isArray(r.role?.aliases) ? r.role.aliases : r.role?.aliases?.en || []).join(", "),
        sourceSectorId,
        sourceSectorName: sourceMapping.current_sector || sourceMapping.sector_ref?.sectorNameEn || "",
        sourceOccupationId,
        sourceOccupationName: sourceMapping.current_occupation || sourceMapping.occupation_ref?.occupationNameEn || "",
        sourceJobRoleId,
        sourceJobRoleName: sourceMapping.current_job_role || sourceMapping.job_role_ref?.jobRoleNameEn || ""
      }
    });
  };
  const openDeleteRole = (r) => setDeleteModal({ open: true, type: "role", id: r._id, name: r.role?.name?.en || r.role?.role_name || r.role?.role_id });

  const closeFamilyModal = () => setFamilyModal({ ...familyModal, open: false });
  const closeGroupModal = () => setGroupModal({ ...groupModal, open: false });
  const closeRoleModal = () => setRoleModal({ ...roleModal, open: false });
  const closeViewModal = () => setViewModal({ open: false, type: "", data: null });

  const filteredFamilies = families.filter((f) => (
    includesSearch(f.family_name || f.name?.en, familySearch)
    || includesSearch(f.family_id, familySearch)
    || includesSearch(f.family_ui_label || f.ui_label, familySearch)
    || includesSearch(f.family_label_hi || f.name?.hi, familySearch)
    || includesSearch(f.family_label_hn || f.name?.hn, familySearch)
  ));

  const filteredGroups = groups.filter((g) => (
    includesSearch(g.role_group_name || g.name?.en, groupSearch)
    || includesSearch(g.role_group_id, groupSearch)
    || includesSearch(g.role_group_ui_label || g.ui_label, groupSearch)
    || includesSearch(g.role_group_label_hi || g.name?.hi, groupSearch)
    || includesSearch(g.role_group_label_hn || g.name?.hn, groupSearch)
  ));

  const filteredRoles = roles.filter((r) => {
    const aliases = aliasesForSearch(r.role?.aliases);
    return includesSearch(r.role?.role_name || r.role?.name?.en, roleSearch)
      || includesSearch(r.role?.role_id, roleSearch)
      || includesSearch(r.role?.role_ui_label || r.role?.ui_label, roleSearch)
      || includesSearch(r.role?.role_label_hi || r.role?.name?.hi, roleSearch)
      || includesSearch(r.role?.role_label_hn || r.role?.name?.hn, roleSearch)
      || includesSearch(r.display?.display_title, roleSearch)
      || includesSearch(r.display?.candidate_display_title, roleSearch)
      || includesSearch(r.display?.employer_display_title, roleSearch)
      || includesSearch(r.source_mapping?.current_sector || sourceLabel(r.source_mapping?.sector_ref), roleSearch)
      || includesSearch(r.source_mapping?.current_occupation || sourceLabel(r.source_mapping?.occupation_ref), roleSearch)
      || includesSearch(r.source_mapping?.current_job_role || sourceLabel(r.source_mapping?.job_role_ref), roleSearch)
      || aliases.some((alias) => includesSearch(alias, roleSearch));
  });

  const getViewRows = () => {
    const item = viewModal.data || {};
    if (viewModal.type === "family") {
      return [
        ["Family ID", item.family_id],
        ["Name", item.family_name || item.name?.en],
        ["UI Label", item.family_ui_label || item.ui_label],
        ["Icon", item.family_icon || item.icon],
        ["Sort Order", item.family_sort_order || item.sort_order],
        ["Mapping ID", item.mappingId],
        ["Family Master ID", item.familyMasterId],
      ];
    }
    if (viewModal.type === "group") {
      return [
        ["Group ID", item.role_group_id],
        ["Name", item.role_group_name || item.name?.en],
        ["UI Label", item.role_group_ui_label || item.ui_label],
        ["Sort Order", item.role_group_sort_order || item.sort_order],
        ["Family ID", item.familyId],
        ["Mapping ID", item.mappingId],
      ];
    }
    const aliases = Array.isArray(item.role?.aliases) ? item.role.aliases : item.role?.aliases?.en || [];
    return [
      ["Role ID", item.role?.role_id],
      ["Name", item.role?.role_name || item.role?.name?.en],
      ["UI Label", item.role?.role_ui_label || item.role?.ui_label],
      ["Description", item.role?.role_description || item.role?.description],
      ["Aliases", aliases.join(", ")],
      ["Display Title", item.display?.display_title],
      ["Candidate Display Title", item.display?.candidate_display_title],
      ["Source Sector", item.source_mapping?.current_sector || item.source_mapping?.sector_ref?.sectorNameEn],
      ["Source Occupation", item.source_mapping?.current_occupation || item.source_mapping?.occupation_ref?.occupationNameEn],
      ["Source Job Role", item.source_mapping?.current_job_role || item.source_mapping?.job_role_ref?.jobRoleNameEn],
      ["Status", item.status],
      ["Mapping ID", item._id],
    ];
  };

  return (
    <Box className="candidate-role-mappings" mt={4}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Candidate Role Mappings</Typography>
      <Grid container spacing={1.5} mb={2}>
        {[
          ["Active NCO roles", coverage?.totalActiveNcoRoles, "text.primary"],
          ["Mapped NCO roles", coverage?.mappedNcoRoleCount, "success.main"],
          ["Unmapped NCO roles", coverage?.unmappedNcoRoleCount, "error.main"],
          ["Candidate roles", coverage?.totalActiveCandidateRoles, "text.primary"],
          ["Invalid/orphan links", coverage?.orphanLinkCount, "error.main"],
        ].map(([label, value, color]) => (
          <Grid item xs={6} md={2} key={label}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={700} color={color}>
                  {value ?? "—"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <h3>Candidate Families ({families.length})</h3>
            <TextField
              size="small"
              fullWidth
              placeholder="Search families"
              value={familySearch}
              onChange={(e) => setFamilySearch(e.target.value)}
              sx={{ mb: 1 }}
              {...searchFieldProps(familySearch, () => setFamilySearch(""))}
            />
            {perms.canCreate && (
              <ColorButton variant="contained" onClick={openCreateFamily} sx={{ width: "98%", mb: 1 }}>
                <AddIcon fontSize="small" />
                <Typography fontSize="14px" fontWeight="600" ml={0.5}>Add Family</Typography>
              </ColorButton>
            )}
            <CardContent sx={{ height: "70vh", overflow: "scroll", px: 0 }}>
              {showLoaderFamilies ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : families.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No families found</Typography>
              ) : filteredFamilies.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No matching families</Typography>
              ) : filteredFamilies.map((f, i) => (
                <Paper
                  key={f.family_id || i}
                  sx={{ my: 0.8, cursor: "pointer", bgcolor: selectedFamily?.family_id === f.family_id ? "#EDF7ED" : "inherit" }}
                  onClick={() => handleSelectFamily(f)}
                >
                  <Box p={2}>
                    <Grid container alignItems="center">
                      <Grid item xs={7}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {(f.family_icon || f.icon) && <span style={{ fontSize: 20 }}>{f.family_icon || f.icon}</span>}
                          <Typography sx={{ textTransform: "capitalize", fontWeight: selectedFamily?.family_id === f.family_id ? 600 : 400 }}>
                            {f.family_name || f.name?.en || f.family_id}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={5}>
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View">
                            <VisibilityIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); setViewModal({ open: true, type: "family", data: f }); }} />
                          </Tooltip>
                          {perms.canUpdate && (
                            <EditIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); openEditFamily(f); }} />
                          )}
                          {perms.canDelete && (
                            <DeleteIcon fontSize="small" color="error" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); openDeleteFamily(f); }} />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <h3>Role Groups</h3>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {selectedFamily ? `Family: ${selectedFamily.family_name || selectedFamily.name?.en || selectedFamily.family_id}` : "Select a family first"}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search groups"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              disabled={!selectedFamily}
              sx={{ mb: 1 }}
              {...searchFieldProps(groupSearch, () => setGroupSearch(""))}
            />
            {selectedFamily && perms.canCreate && (
              <ColorButton variant="contained" onClick={openCreateGroup} sx={{ width: "98%", mb: 1 }}>
                <AddIcon fontSize="small" />
                <Typography fontSize="14px" fontWeight="600" ml={0.5}>Add Group</Typography>
              </ColorButton>
            )}
            <CardContent sx={{ height: "70vh", overflow: "scroll", px: 0 }}>
              {showLoaderGroups ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : !selectedFamily ? (
                <Typography textAlign="center" color="text.secondary" py={4}>Select a family</Typography>
              ) : groups.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No groups found</Typography>
              ) : filteredGroups.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No matching groups</Typography>
              ) : filteredGroups.map((g, i) => (
                <Paper
                  key={g.role_group_id || i}
                  sx={{ my: 0.8, cursor: "pointer", bgcolor: selectedGroup?.role_group_id === g.role_group_id ? "#EDF7ED" : "inherit" }}
                  onClick={() => handleSelectGroup(g)}
                >
                  <Box p={2}>
                    <Grid container alignItems="center">
                      <Grid item xs={7}>
                        <Typography sx={{ textTransform: "capitalize", fontWeight: selectedGroup?.role_group_id === g.role_group_id ? 600 : 400 }}>
                          {g.role_group_name || g.name?.en || g.role_group_id}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View">
                            <VisibilityIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); setViewModal({ open: true, type: "group", data: g }); }} />
                          </Tooltip>
                          {perms.canUpdate && (
                            <EditIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); openEditGroup(g); }} />
                          )}
                          {perms.canDelete && (
                            <DeleteIcon fontSize="small" color="error" sx={{ cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); openDeleteGroup(g); }} />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={4}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <h3>Job Roles</h3>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {selectedGroup ? `Group: ${selectedGroup.role_group_name || selectedGroup.name?.en || selectedGroup.role_group_id}` : "Select a group first"}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search roles"
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
              disabled={!selectedGroup}
              sx={{ mb: 1 }}
              {...searchFieldProps(roleSearch, () => setRoleSearch(""))}
            />
            {selectedGroup && perms.canCreate && (
              <ColorButton variant="contained" onClick={openCreateRole} sx={{ width: "98%", mb: 1 }}>
                <AddIcon fontSize="small" />
                <Typography fontSize="14px" fontWeight="600" ml={0.5}>Add Role</Typography>
              </ColorButton>
            )}
            <CardContent sx={{ height: "70vh", overflow: "scroll", px: 0 }}>
              {showLoaderRoles ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : !selectedGroup ? (
                <Typography textAlign="center" color="text.secondary" py={4}>Select a group</Typography>
              ) : roles.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No roles found</Typography>
              ) : filteredRoles.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" py={4}>No matching roles</Typography>
              ) : filteredRoles.map((r, i) => (
                <Paper
                  key={r.roleItemId || r._id || r.role?.role_id || i}
                  onClick={() => handleSelectRole(r)}
                  sx={{
                    my: 0.8,
                    cursor: r.roleItemId ? "pointer" : "default",
                    border: selectedRole?.roleItemId && selectedRole.roleItemId === r.roleItemId ? "2px solid #2e7d32" : "1px solid transparent",
                    bgcolor: selectedRole?.roleItemId && selectedRole.roleItemId === r.roleItemId ? "#EDF7ED" : "inherit",
                  }}
                >
                  <Box p={2}>
                    <Grid container alignItems="center">
                      <Grid item xs={7}>
                        <Typography sx={{ textTransform: "capitalize" }}>
                          {r.role?.name?.en || r.role?.role_name || r.role?.role_id}
                        </Typography>
                        {(Array.isArray(r.role?.aliases) ? r.role.aliases : r.role?.aliases?.en || []).length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(Array.isArray(r.role?.aliases) ? r.role.aliases : r.role?.aliases?.en || []).slice(0, 3).join(", ")}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          sx={{ mt: 0.5 }}
                          color={r.roleItemId ? "success" : "warning"}
                          label={r.roleItemId ? "Canonical candidate role" : "Canonical role not synced"}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View">
                            <VisibilityIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(event) => { event.stopPropagation(); setViewModal({ open: true, type: "role", data: r }); }} />
                          </Tooltip>
                          {perms.canUpdate && (
                            <EditIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }}
                              onClick={(event) => { event.stopPropagation(); openEditRole(r); }} />
                          )}
                          {perms.canDelete && (
                            <DeleteIcon fontSize="small" color="error" sx={{ cursor: "pointer" }}
                              onClick={(event) => { event.stopPropagation(); openDeleteRole(r); }} />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Box>
                  <Typography variant="h6">NCO roles linked to candidate role</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRole
                      ? `${selectedRole.role?.role_name || selectedRole.role?.name?.en || selectedRole.role?.role_id} (${roleLinks.length})`
                      : "Select a canonical candidate role above"}
                  </Typography>
                </Box>
                {mappingLoading && <CircularProgress size={22} />}
              </Box>
              <Box sx={{ maxHeight: 430, overflowY: "auto" }}>
                {!selectedRole ? (
                  <Typography color="text.secondary" py={3} textAlign="center">No candidate role selected</Typography>
                ) : !selectedRole.roleItemId ? (
                  <Typography color="warning.main" py={3} textAlign="center">This legacy role is not present in the canonical taxonomy.</Typography>
                ) : roleLinks.length === 0 ? (
                  <Typography color="text.secondary" py={3} textAlign="center">No direct NCO roles are linked. Group and family fallback will still apply.</Typography>
                ) : roleLinks.map((link) => (
                  <Paper key={link._id || link.ncoJobRole?._id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" gap={1} alignItems="center">
                      <Box>
                        <Typography fontWeight={600}>{link.ncoJobRole?.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[link.ncoJobRole?.occupation?.name, link.ncoJobRole?.ncoCode].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                      {perms.canDelete && (
                        <Button
                          size="small"
                          color="error"
                          disabled={mappingLoading}
                          onClick={() => handleUnmapNcoRole(link.ncoJobRole?._id)}
                        >
                          Unmap
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{showMappedNcoRoles ? "Mapped NCO links" : "Unmapped NCO queue"}</Typography>
                <Box display="flex" gap={0.5}>
                  <Button
                    size="small"
                    variant={!showMappedNcoRoles ? "contained" : "outlined"}
                    onClick={() => {
                      setShowMappedNcoRoles(false);
                      loadUnmappedNcoRoles(ncoSearch, ncoSectorId, ncoOccupationId, false);
                    }}
                  >
                    Unmapped
                  </Button>
                  <Button
                    size="small"
                    variant={showMappedNcoRoles ? "contained" : "outlined"}
                    onClick={() => {
                      setShowMappedNcoRoles(true);
                      loadUnmappedNcoRoles(ncoSearch, ncoSectorId, ncoOccupationId, true);
                    }}
                  >
                    Mapped / Reassign
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={1} mb={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Search role, occupation, NCO code"
                    value={ncoSearch}
                    onChange={(event) => setNcoSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") loadUnmappedNcoRoles(ncoSearch, ncoSectorId, ncoOccupationId, showMappedNcoRoles);
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Sector"
                    value={ncoSectorId}
                    onChange={(event) => {
                      const sectorId = event.target.value;
                      setNcoSectorId(sectorId);
                      setNcoOccupationId("");
                      if (sectorId) loadSourceOccupations(sectorId);
                      else setOccupations([]);
                    }}
                  >
                    <MenuItem value="">All sectors</MenuItem>
                    {sectors.map(item => <MenuItem key={item._id} value={item._id}>{item.sectorNameEn}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Occupation"
                    value={ncoOccupationId}
                    disabled={!ncoSectorId}
                    onChange={(event) => setNcoOccupationId(event.target.value)}
                  >
                    <MenuItem value="">All occupations</MenuItem>
                    {occupations.map(item => <MenuItem key={item._id} value={item._id}>{item.occupationNameEn}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">Showing up to 50 matching NCO roles</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => loadUnmappedNcoRoles(ncoSearch, ncoSectorId, ncoOccupationId, showMappedNcoRoles)}
                >
                  Search / Refresh
                </Button>
              </Box>
              <Box sx={{ maxHeight: 430, overflowY: "auto" }}>
                {unmappedNcoRoles.length === 0 ? (
                  <Typography color={showMappedNcoRoles ? "text.secondary" : "success.main"} py={3} textAlign="center">
                    {showMappedNcoRoles ? "No mapped NCO roles found for these filters." : "No unmapped NCO roles found for these filters."}
                  </Typography>
                ) : unmappedNcoRoles.map((link) => (
                  <Paper key={link.ncoJobRole?._id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Box display="flex" justifyContent="space-between" gap={1} alignItems="center">
                      <Box>
                        <Typography fontWeight={600}>{link.ncoJobRole?.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[link.ncoJobRole?.occupation?.name, link.ncoJobRole?.ncoCode].filter(Boolean).join(" · ")}
                        </Typography>
                        {link.candidateRole && (
                          <Typography variant="caption" color="primary" display="block">
                            Currently linked to: {link.candidateRole.name} ({link.candidateRole.roleId})
                          </Typography>
                        )}
                      </Box>
                      {perms.canUpdate && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            !selectedRole?.roleItemId
                            || mappingLoading
                            || String(link.candidateRole?._id || "") === String(selectedRole?.roleItemId || "")
                          }
                          onClick={() => handleMapNcoRole(link.ncoJobRole?._id)}
                        >
                          {String(link.candidateRole?._id || "") === String(selectedRole?.roleItemId || "")
                            ? "Already selected"
                            : link.isMapped ? "Reassign to selected" : "Map to selected"}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ModalComponent
        openModal={familyModal.open}
        setOpenModal={() => { }}
        modalTitle={familyModal.editId ? "Update Family" : "Create Family"}
        modalWidth="40%"
        handleOnCloseBtnClick={closeFamilyModal}
      >
        <Formik
          enableReinitialize
          initialValues={familyModal.initial}
          validationSchema={familyValidation}
          onSubmit={(values) => {
            handleSubmitMapping("family", values, familyModal.editId);
            closeFamilyModal();
          }}
        >
          {({ errors, values, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Box p={2} display="flex" flexDirection="column" gap={2}>
                <TextField size="small" label="Family ID *" name="familyId" value={values.familyId} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.familyId)} helperText={errors.familyId} />
                <TextField size="small" label="Name (English) *" name="nameEn" value={values.nameEn} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.nameEn)} helperText={errors.nameEn} />
                <TextField size="small" label="Name (Hindi)" name="nameHi" value={values.nameHi} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="Name (Hinglish)" name="nameHn" value={values.nameHn} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="UI Label" name="uiLabel" value={values.uiLabel} onChange={handleChange} />
                <TextField size="small" label="Icon" name="icon" value={values.icon} onChange={handleChange} />
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(10, 1fr)"
                  gap={0.75}
                  sx={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}
                >
                  {FAMILY_ICON_EMOJIS.map((emoji) => (
                    <Button
                      key={emoji}
                      variant={values.icon === emoji ? "contained" : "outlined"}
                      onClick={() => setFieldValue("icon", emoji)}
                      sx={{ minWidth: 0, p: 0.75, fontSize: 18, lineHeight: 1 }}
                    >
                      {emoji}
                    </Button>
                  ))}
                </Box>
                <TextField size="small" label="Sort Order" name="sortOrder" type="number" value={values.sortOrder} onChange={handleChange} />
              </Box>
              <Box sx={{ borderTop: "1px solid #bdbdbd" }} p={1} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={closeFamilyModal} disableElevation variant="contained" color="error">Cancel</Button>
                <Button type="submit" disableElevation variant="contained">{familyModal.editId ? "Update" : "Create"}</Button>
              </Box>
            </form>
          )}
        </Formik>
      </ModalComponent>

      <ModalComponent
        openModal={groupModal.open}
        setOpenModal={() => { }}
        modalTitle={groupModal.editId ? "Update Group" : "Create Group"}
        modalWidth="40%"
        handleOnCloseBtnClick={closeGroupModal}
      >
        <Formik
          enableReinitialize
          initialValues={groupModal.initial}
          validationSchema={groupValidation}
          onSubmit={(values) => {
            handleSubmitMapping("group", values, groupModal.editId);
            closeGroupModal();
          }}
        >
          {({ errors, values, handleBlur, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Box p={2} display="flex" flexDirection="column" gap={2}>
                <TextField size="small" label="Group ID *" name="roleGroupId" value={values.roleGroupId} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.roleGroupId)} helperText={errors.roleGroupId} />
                <TextField size="small" label="Name (English) *" name="nameEn" value={values.nameEn} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.nameEn)} helperText={errors.nameEn} />
                <TextField size="small" label="Name (Hindi)" name="nameHi" value={values.nameHi} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="Name (Hinglish)" name="nameHn" value={values.nameHn} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="UI Label" name="uiLabel" value={values.uiLabel} onChange={handleChange} />
                <TextField size="small" label="Sort Order" name="sortOrder" type="number" value={values.sortOrder} onChange={handleChange} />
              </Box>
              <Box sx={{ borderTop: "1px solid #bdbdbd" }} p={1} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={closeGroupModal} disableElevation variant="contained" color="error">Cancel</Button>
                <Button type="submit" disableElevation variant="contained">{groupModal.editId ? "Update" : "Create"}</Button>
              </Box>
            </form>
          )}
        </Formik>
      </ModalComponent>

      <ModalComponent
        openModal={roleModal.open}
        setOpenModal={() => { }}
        modalTitle={roleModal.editId ? "Update Role" : "Create Role"}
        modalWidth="40%"
        handleOnCloseBtnClick={closeRoleModal}
      >
        <Formik
          enableReinitialize
          initialValues={roleModal.initial}
          validationSchema={roleValidation}
          onSubmit={(values) => {
            handleSubmitMapping("role", values, roleModal.editId);
            closeRoleModal();
          }}
        >
          {({ errors, values, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Box p={2} display="flex" flexDirection="column" gap={2}>
                <TextField size="small" label="Role ID *" name="roleId" value={values.roleId} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.roleId)} helperText={errors.roleId} />
                <TextField size="small" label="Name (English) *" name="nameEn" value={values.nameEn} onChange={handleChange} onBlur={handleBlur} error={Boolean(errors.nameEn)} helperText={errors.nameEn} />
                <TextField size="small" label="Name (Hindi)" name="nameHi" value={values.nameHi} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="Name (Hinglish)" name="nameHn" value={values.nameHn} onChange={handleChange} onBlur={handleBlur} />
                <TextField size="small" label="UI Label" name="uiLabel" value={values.uiLabel} onChange={handleChange} />
                <TextField size="small" label="Description" name="description" value={values.description} onChange={handleChange} multiline rows={2} />
                <TextField size="small" label="Aliases (comma separated)" name="aliases" value={values.aliases} onChange={handleChange} />
                <Typography variant="subtitle2" fontWeight={600}>Optional initial NCO mapping</Typography>
                <Typography variant="caption" color="text.secondary">
                  You can create the candidate role without an NCO role and link one or many NCO roles from the queue afterward.
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Sector"
                  name="sourceSectorId"
                  value={values.sourceSectorId}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    const sectorId = event.target.value;
                    const selected = sectors.find(item => item._id === sectorId);
                    setFieldValue("sourceSectorId", sectorId);
                    setFieldValue("sourceSectorName", selected?.sectorNameEn || "");
                    setFieldValue("sourceOccupationId", "");
                    setFieldValue("sourceOccupationName", "");
                    setFieldValue("sourceJobRoleId", "");
                    setFieldValue("sourceJobRoleName", "");
                    setJobRoles([]);
                    loadSourceOccupations(sectorId);
                  }}
                  error={Boolean(errors.sourceSectorId)}
                  helperText={errors.sourceSectorId || (showLoaderSectors ? "Loading sectors..." : "")}
                >
                  <MenuItem value="">No initial sector</MenuItem>
                  {values.sourceSectorId && !sectors.some(item => item._id === values.sourceSectorId) && (
                    <MenuItem value={values.sourceSectorId}>{values.sourceSectorName || "Selected sector"}</MenuItem>
                  )}
                  {sectors.map(item => (
                    <MenuItem key={item._id} value={item._id}>{item.sectorNameEn}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Occupation"
                  name="sourceOccupationId"
                  value={values.sourceOccupationId}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    const occupationId = event.target.value;
                    const selected = occupations.find(item => item._id === occupationId);
                    setFieldValue("sourceOccupationId", occupationId);
                    setFieldValue("sourceOccupationName", selected?.occupationNameEn || "");
                    setFieldValue("sourceJobRoleId", "");
                    setFieldValue("sourceJobRoleName", "");
                    loadSourceJobRoles(occupationId);
                  }}
                  disabled={!values.sourceSectorId}
                  error={Boolean(errors.sourceOccupationId)}
                  helperText={errors.sourceOccupationId || (showLoaderOccupations ? "Loading occupations..." : "")}
                >
                  <MenuItem value="">No initial occupation</MenuItem>
                  {values.sourceOccupationId && !occupations.some(item => item._id === values.sourceOccupationId) && (
                    <MenuItem value={values.sourceOccupationId}>{values.sourceOccupationName || "Selected occupation"}</MenuItem>
                  )}
                  {occupations.map(item => (
                    <MenuItem key={item._id} value={item._id}>{item.occupationNameEn}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Job Role"
                  name="sourceJobRoleId"
                  value={values.sourceJobRoleId}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    const jobRoleId = event.target.value;
                    const selected = jobRoles.find(item => item._id === jobRoleId);
                    setFieldValue("sourceJobRoleId", jobRoleId);
                    setFieldValue("sourceJobRoleName", selected?.jobRoleNameEn || "");
                  }}
                  disabled={!values.sourceOccupationId}
                  error={Boolean(errors.sourceJobRoleId)}
                  helperText={errors.sourceJobRoleId || (showLoaderJobRoles ? "Loading job roles..." : "")}
                >
                  <MenuItem value="">No initial NCO job role</MenuItem>
                  {values.sourceJobRoleId && !jobRoles.some(item => item._id === values.sourceJobRoleId) && (
                    <MenuItem value={values.sourceJobRoleId}>{values.sourceJobRoleName || "Selected job role"}</MenuItem>
                  )}
                  {jobRoles.map(item => (
                    <MenuItem key={item._id} value={item._id}>{item.jobRoleNameEn}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ borderTop: "1px solid #bdbdbd" }} p={1} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={closeRoleModal} disableElevation variant="contained" color="error">Cancel</Button>
                <Button type="submit" disableElevation variant="contained">{roleModal.editId ? "Update" : "Create"}</Button>
              </Box>
            </form>
          )}
        </Formik>
      </ModalComponent>

      <ModalComponent
        openModal={viewModal.open}
        setOpenModal={() => { }}
        modalTitle={`View ${viewModal.type ? viewModal.type.charAt(0).toUpperCase() + viewModal.type.slice(1) : "Details"}`}
        modalWidth="45%"
        handleOnCloseBtnClick={closeViewModal}
      >
        <Box p={2} display="flex" flexDirection="column" gap={1.25}>
          {getViewRows().filter(([, value]) => value !== undefined && value !== null && value !== "").map(([label, value]) => (
            <Box key={label} display="grid" gridTemplateColumns="180px 1fr" gap={2} alignItems="start">
              <Typography fontWeight={600} color="text.secondary">{label}</Typography>
              <Typography sx={{ wordBreak: "break-word" }}>{String(value)}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ borderTop: "1px solid #bdbdbd" }} p={1} display="flex" justifyContent="flex-end">
          <Button onClick={closeViewModal} disableElevation variant="contained">Close</Button>
        </Box>
      </ModalComponent>

      <SureYouWantToDeletModal
        openDeleteModal={deleteModal.open}
        setOpenDeleteModal={() => setDeleteModal({ ...deleteModal, open: false })}
        modalTitle={`Delete ${deleteModal.type}`}
        dialogBody={`Are you sure you want to delete "${deleteModal.name}"?`}
        onYesClick={handleDelete}
        endIcon={deleteLoading ? <CircularProgress sx={{ color: "#fff" }} size={20} /> : ""}
        disabled={deleteLoading}
      />
    </Box>
  );
};

export default CandidateRoleMappings;
